import { useEffect, useState } from "react";

/**
 * Hook that returns true only after client-side hydration is complete.
 *
 * This hook prevents hydration mismatches by always returning false during
 * server-side rendering and the initial client render, then returns true
 * after the component mounts.
 *
 * @returns {boolean} false during SSR and first render, true after mount
 *
 * @example
 * // Use for conditional rendering to prevent hydration mismatches
 * function MyComponent() {
 *   const isClient = useIsClient();
 *
 *   return (
 *     <div>
 *       {isClient && <ClientOnlyComponent />}
 *     </div>
 *   );
 * }
 *
 * @example
 * // Use to safely access browser APIs
 * function MyComponent() {
 *   const isClient = useIsClient();
 *   const gtmId = isClient ? window.ENV.GTM_ID : null;
 *   // ...
 * }
 */
export function useIsClient(): boolean {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return isClient;
}

/**
 * Synchronous check for browser environment.
 *
 * Use this constant in:
 * - useEffect guards (not conditional rendering)
 * - Utility functions outside React components
 * - Non-render logic that needs immediate browser detection
 *
 * ⚠️ WARNING: Do NOT use for conditional rendering in React components.
 * Use the useIsClient() hook instead to prevent hydration mismatches.
 *
 * @example
 * // ✅ GOOD: useEffect guard
 * useEffect(() => {
 *   if (!isBrowser) return;
 *   window.addEventListener('resize', handler);
 * }, []);
 *
 * @example
 * // ✅ GOOD: Utility function
 * export function getLocalStorage(key: string) {
 *   if (!isBrowser) return null;
 *   return localStorage.getItem(key);
 * }
 *
 * @example
 * // ❌ BAD: Conditional rendering (causes hydration mismatch)
 * return <div>{isBrowser && <ClientComponent />}</div>;
 * // Use useIsClient() instead!
 */
export const isBrowser = typeof window !== "undefined";
